/**
 * Returns a contrasting foreground color (light or dark) for a given background
 * using relative luminance (WCAG). Use when mapping theme to CSS vars so text
 * stays readable on any institution background.
 */
const hexToRgb = (hex: string): [number, number, number] => {
  const clean = hex.replace(/^#/, "");
  if (clean.length === 3) {
    const r = parseInt(clean[0] + clean[0], 16);
    const g = parseInt(clean[1] + clean[1], 16);
    const b = parseInt(clean[2] + clean[2], 16);
    return [r, g, b];
  }
  if (clean.length === 6) {
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return [r, g, b];
  }
  return [0, 0, 0];
};

const relativeLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

export interface ContrastForegroundOptions {
  light?: string;
  dark?: string;
}

/**
 * Returns a foreground color that contrasts with the given background.
 * Uses WCAG relative luminance; if background is dark (luminance < 0.5),
 * returns the light color, otherwise the dark color.
 */
export const getContrastForeground = (
  backgroundHex: string,
  options: ContrastForegroundOptions = {},
): string => {
  const light = options.light ?? "#FFFFFF";
  const dark = options.dark ?? "#000000";
  const [r, g, b] = hexToRgb(backgroundHex);
  const luminance = relativeLuminance(r, g, b);
  return luminance < 0.5 ? light : dark;
};
